/**
 * SDE Phase 3 (s88) — persists the FileSystemFileHandle for the .oesif Secure File across
 * page reloads, in IndexedDB. This enables Chrome/Edge auto-reconnect: on login, retrieve
 * the handle, request a one-click permission, read + decrypt + load.
 *
 * 🔒 Stores ONLY the opaque handle (a browser reference to the file location) — NEVER the
 * file contents or Category A values. Those live in memory only (SensitiveDataSessionStore)
 * and on disk in the encrypted file.
 *
 * Safari/Firefox have no File System Access API → handles can't be persisted there; those
 * browsers use the manual `<input type="file">` open path every time.
 */

const DB_NAME = "oe-secure-file";
const STORE_NAME = "handles";
const HANDLE_KEY = "oesif-file-handle";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Persist the handle for cross-reload auto-reconnect. No-op if IndexedDB is unavailable. */
export async function saveHandle(handle: FileSystemFileHandle): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(handle, HANDLE_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // Non-fatal — auto-reconnect just won't be available; manual open still works.
  }
}

/** Retrieve the persisted handle, or null. */
export async function getHandle(): Promise<FileSystemFileHandle | null> {
  if (typeof indexedDB === "undefined") return null;
  try {
    const db = await openDb();
    const handle = await new Promise<FileSystemFileHandle | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(HANDLE_KEY);
      req.onsuccess = () => resolve((req.result as FileSystemFileHandle) ?? null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return handle;
  } catch {
    return null;
  }
}

/** Drop the persisted handle (file moved/deleted, permission denied, logout). */
export async function clearHandle(): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(HANDLE_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* non-fatal */
  }
}

/**
 * Ensure read/write permission on a persisted handle. queryPermission first (silent when
 * already granted), then requestPermission (the one-click browser prompt) if needed.
 * Returns true when usable. `silent: true` skips the prompt (query only) — for the on-load
 * auto-reconnect check where we don't want to force a prompt before the user acts.
 */
export async function ensureHandlePermission(
  handle: FileSystemFileHandle,
  opts: { silent?: boolean } = {},
): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const h = handle as any;
  const descriptor = { mode: "readwrite" as const };
  try {
    if (typeof h.queryPermission === "function") {
      const state = await h.queryPermission(descriptor);
      if (state === "granted") return true;
      if (opts.silent) return false;
    } else if (opts.silent) {
      return false;
    }
    if (typeof h.requestPermission === "function") {
      return (await h.requestPermission(descriptor)) === "granted";
    }
    return false;
  } catch {
    return false;
  }
}
